import { ConsentFormDefinition } from '../types';

export const informedConsent: ConsentFormDefinition = {
  formKey: 'informed-consent',
  title: 'Informed Consent for Treatment',
  pdfTitle: 'Informed Consent for Behavioral Health Treatment',
  intro:
    'This form explains the types of therapy and behavioral health treatment available through {providerName}, including potential benefits and risks. Please read this carefully and ask questions before signing.',
  sections: [
    {
      id: 'patient',
      navLabel: 'Patient Info',
      heading: 'Your Information',
      blocks: [
        {
          type: 'field-group',
          layout: '2-1',
          fields: [
            {
              type: 'text',
              id: 'firstName',
              label: 'First Name',
              placeholder: 'First name',
              required: true,
            },
            {
              type: 'text',
              id: 'middleInitial',
              label: 'Middle Initial (optional)',
              placeholder: 'M.I.',
            },
            {
              type: 'text',
              id: 'lastName',
              label: 'Last Name',
              placeholder: 'Last name',
              required: true,
            },
            {
              type: 'date',
              id: 'dob',
              label: 'Date of Birth',
              required: true,
            },
          ],
        },
      ],
    },
    {
      id: 'provider-info',
      navLabel: 'Your Therapist',
      heading: "Your Therapist's Information",
      blocks: [
        {
          type: 'paragraph',
          text: 'You have the right to know about the person providing your treatment:',
          bold: true,
        },
        {
          type: 'field-group',
          layout: '2-1',
          pdfTable: true,
          fields: [
            {
              type: 'readonly',
              id: 'providerName',
              label: 'Provider Name',
              value: 'providerName',
            },
            {
              type: 'text',
              id: 'providerLicense',
              label: 'License Type & Number',
              placeholder: 'e.g., LCSW #12345, LPC #67890',
              pdfMinWidth: 140,
            },
            {
              type: 'readonly',
              id: 'providerSpecialty',
              label: 'Primary Training/Specialty',
              value: 'providerSpeciality',
            },
          ],
        },
        {
          type: 'paragraph',
          text: "You can verify your therapist's license by contacting your state's licensing board. Ask your therapist if they are a supervised intern or associate, or if they are fully licensed.",
        },
      ],
    },
    {
      id: 'what-is-therapy',
      navLabel: 'What Is Therapy?',
      heading: 'What Is Psychotherapy and Counseling?',
      blocks: [
        {
          type: 'paragraph',
          text: 'Therapy is a collaborative process where you and your therapist work together to understand your concerns and develop skills to address them. Your therapist will listen, ask questions, and help you find solutions.',
        },
        {
          type: 'paragraph',
          text: 'Common approaches used in therapy:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Cognitive Behavioral Therapy (CBT): Focuses on how your thoughts, feelings, and behaviors are connected; teaches skills to change unhelpful patterns',
            'Psychodynamic Therapy: Explores how past experiences and unconscious patterns affect your current relationships and emotions',
            'Acceptance and Commitment Therapy (ACT): Helps you accept difficult thoughts and feelings while living according to your values',
            'Dialectical Behavior Therapy (DBT): Teaches mindfulness, distress tolerance, emotion regulation, and interpersonal effectiveness; often used for intense emotions or self-harm',
            'Family Systems Therapy: Works with families to improve communication and resolve conflict',
            'Solution-Focused Therapy: Emphasizes identifying strengths and building practical solutions',
            'Supportive Counseling: Provides emotional support, validation, and coping strategies',
          ],
        },
        {
          type: 'paragraph',
          text: 'Your therapist may combine approaches based on your needs. The specific approach will be discussed with you at the start of treatment.',
        },
      ],
    },
    {
      id: 'treatment-goals',
      navLabel: 'Treatment Goals',
      heading: 'Developing Your Treatment Goals',
      blocks: [
        {
          type: 'paragraph',
          text: 'At your first appointment, you and your therapist will discuss:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            "Why you're seeking therapy and what brings you in",
            'Your concerns, symptoms, or challenges',
            'What you hope to achieve through therapy',
            'Your personal values and what matters most to you',
            "Any past therapy experiences (what worked, what didn't)",
            'Your background, relationships, and current life situation',
          ],
        },
        {
          type: 'paragraph',
          text: 'Together, you\'ll create specific, measurable goals. For example: "Reduce anxiety symptoms," "Improve communication with my partner," or "Develop better coping skills for stress." Your therapist will check your progress regularly.',
        },
      ],
    },
    {
      id: 'session-process',
      navLabel: 'What Happens in Sessions',
      heading: 'How Therapy Sessions Work',
      blocks: [
        {
          type: 'paragraph',
          text: 'Sessions are typically:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            '45-50 minutes long',
            'Scheduled weekly or as agreed with your therapist',
            'Private and confidential',
            'Focused on your goals and concerns',
          ],
        },
        {
          type: 'paragraph',
          text: 'What to expect:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Your therapist will ask questions and listen carefully',
            'You will talk about your thoughts, feelings, and experiences',
            'Your therapist may teach you new skills or coping strategies',
            'You may have "homework" between sessions (journaling, practicing skills, etc.)',
            'Sessions may be emotionally challenging—talking about difficult topics is normal',
            "You set the pace; you can share as much or as little as you're comfortable with",
          ],
        },
      ],
    },
    {
      id: 'benefits',
      navLabel: 'Benefits of Therapy',
      heading: 'Potential Benefits of Therapy',
      blocks: [
        {
          type: 'paragraph',
          text: 'Therapy can help with:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Reducing anxiety, depression, or other emotional symptoms',
            'Understanding yourself better and why you react the way you do',
            'Improving relationships and communication skills',
            'Developing coping skills for stress, trauma, or life challenges',
            'Increasing self-confidence and self-esteem',
            'Resolving past conflicts or trauma',
            'Making decisions about major life changes',
            'Achieving personal goals and living according to your values',
          ],
        },
        {
          type: 'paragraph',
          text: 'However, therapy is not guaranteed to solve all problems. Progress depends on your effort, openness, and willingness to practice new skills outside of sessions.',
        },
      ],
    },
    {
      id: 'risks',
      navLabel: 'Risks of Therapy',
      heading: 'Potential Risks and Discomforts of Therapy',
      blocks: [
        {
          type: 'paragraph',
          text: 'While therapy is generally helpful, there are some potential risks:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Temporary increase in emotional discomfort: Talking about difficult topics may cause temporary sadness, anxiety, or other uncomfortable feelings',
            'Relationship changes: As you change and grow, some relationships may shift or improve, while others may become difficult or end',
            'Slower progress than expected: Some issues take longer to resolve than anticipated',
            "Medication side effects (if prescribed): If you're prescribed medication, there may be side effects to discuss with your psychiatrist",
            'Realization of painful truths: You may discover things about yourself or your relationships that are hard to accept',
            'Therapy not being the right fit: You and your therapist may not work well together, and changing providers may be necessary',
          ],
        },
        {
          type: 'paragraph',
          text: 'These risks can be minimized through open communication with your therapist. Tell your therapist immediately if you feel uncomfortable, worse, or that therapy is not helping.',
        },
      ],
    },
    {
      id: 'alternatives',
      navLabel: 'Alternatives',
      heading: 'Alternatives to Therapy',
      blocks: [
        {
          type: 'paragraph',
          text: 'Therapy is not the only way to address mental health concerns. Other options include:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Psychiatric medication: Medication alone can help some conditions; usually combined with therapy for best results',
            'Support groups: Connecting with others facing similar challenges',
            'Self-help resources: Books, apps, online courses about mental health and coping skills',
            'Coaching or life coaching: Non-therapeutic support for personal development or life changes',
            'Hospitalization or residential treatment: For severe symptoms or crisis situations',
            'No treatment: Some people choose to manage on their own without professional help',
          ],
        },
        {
          type: 'paragraph',
          text: 'Your therapist can discuss which options might be best for you.',
        },
      ],
    },
    {
      id: 'psychiatric-medication',
      navLabel: 'Medication (if applicable)',
      heading: 'Psychiatric Medication (If Recommended)',
      blocks: [
        {
          type: 'paragraph',
          text: 'If your therapist or psychiatrist recommends medication:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'The prescribing doctor will explain the medication, how it works, and potential side effects',
            'You will discuss benefits and risks specific to your situation',
            'You will be monitored regularly for effectiveness and side effects',
            'Medication is optional—you can choose not to take it, but the prescriber should understand your reasons',
            'Some conditions respond better to medication + therapy combined than either alone',
          ],
        },
        {
          type: 'paragraph',
          text: 'If medication is prescribed, a separate medication consent form will be provided by the prescribing doctor.',
        },
      ],
    },
    {
      id: 'telehealth-specifics',
      navLabel: 'Telehealth Specifics',
      heading: 'Special Considerations for Telehealth Therapy',
      blocks: [
        {
          type: 'paragraph',
          text: 'Research shows telehealth therapy is as effective as in-person therapy for most issues. However:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'The therapist cannot perform a physical examination if needed',
            'Internet disruptions may interrupt sessions',
            'Some issues may be better addressed in person (you and your therapist can discuss)',
            'Your therapist may recommend in-person care if telehealth is not sufficient',
            'You are responsible for finding a private space to ensure confidentiality',
          ],
        },
      ],
    },
    {
      id: 'duration',
      navLabel: 'Treatment Duration',
      heading: 'How Long Will Therapy Last?',
      blocks: [
        {
          type: 'paragraph',
          text: 'The length of therapy varies based on:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'The nature and complexity of your concerns',
            'How quickly you progress toward your goals',
            'Your commitment to attending sessions and practicing new skills',
            'Your openness and willingness to engage in the process',
          ],
        },
        {
          type: 'paragraph',
          text: 'Some people benefit from short-term therapy (8-12 sessions), while others prefer longer-term therapy for ongoing support. You and your therapist will review progress regularly and adjust the treatment plan as needed. You can stop therapy anytime.',
        },
      ],
    },
    {
      id: 'fees-payment',
      navLabel: 'Cost & Payment',
      heading: 'Cost and Payment',
      blocks: [
        {
          type: 'paragraph',
          text: 'Your cost depends on:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Your health insurance coverage (copay, deductible, coinsurance)',
            "Whether you're using a sliding scale (reduced fees based on income)",
            'Out-of-pocket payment if not using insurance',
          ],
        },
        {
          type: 'paragraph',
          text: 'Before your first session:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Ask about your cost and payment options',
            'Verify your insurance coverage with your provider',
            'Ask about cancellation/no-show policies',
            'Understand when payment is due (before or after session)',
          ],
        },
        {
          type: 'paragraph',
          text: 'If cost is a barrier, discuss this with your therapist. Many providers offer sliding scales or payment plans.',
        },
      ],
    },
    {
      id: 'confidentiality-limits',
      navLabel: 'Confidentiality Limits',
      heading: 'Limits of Confidentiality (When Therapist Must Share Information)',
      blocks: [
        {
          type: 'paragraph',
          text: 'Everything you tell your therapist is confidential, EXCEPT in these situations:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You tell your therapist you plan to hurt or kill yourself—therapist may contact emergency services, hospitalize you, or notify family/emergency contacts',
            'You tell your therapist you plan to hurt or kill someone else—therapist may contact police and notify the person at risk',
            'You disclose abuse or neglect of a child, elder, or dependent adult—therapist MUST report to authorities',
            'A court orders your therapist to share information through a subpoena or court order—therapist must comply',
            'You are in group therapy—other group members are not bound by confidentiality',
            'Your therapist needs to consult with a supervisor or colleague for your care—information will be de-identified when possible',
            'You file a complaint against your therapist—your records may be reviewed by licensing boards',
            'Insurance company requires information to process claims—payment information is necessary',
          ],
        },
        {
          type: 'paragraph',
          text: 'Your therapist will tell you as much as possible before breaking confidentiality, except in emergency situations where immediate action is needed to protect safety.',
        },
      ],
    },
    {
      id: 'questions',
      navLabel: 'Your Right to Ask Questions',
      heading: 'You Have the Right to Ask Questions',
      blocks: [
        {
          type: 'paragraph',
          text: 'Before or at any time during therapy, you can ask your therapist:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'What is their training, experience, and license?',
            'What approach will they use and why?',
            'What are the specific risks and benefits for your situation?',
            'How will they measure progress?',
            'What can you do to help yourself between sessions?',
            'Is this therapy working? Should we change approaches?',
            "Can I switch therapists if this isn't working?",
            "What should I do if I'm in crisis between sessions?",
          ],
        },
        {
          type: 'paragraph',
          text: 'A good therapist will welcome your questions and answer clearly.',
        },
      ],
    },
    {
      id: 'right-to-refuse',
      navLabel: 'Your Rights',
      heading: 'You Have Rights in Therapy',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Right to refuse or stop therapy anytime, for any reason',
            'Right to request a different therapist',
            'Right to a second opinion from another therapist',
            'Right to access your mental health records',
            'Right to request your records be sent to another provider',
            'Right to request changes or corrections to your record',
            'Right to file a complaint with your state licensing board if you feel mistreated',
            'Right to know about conflicts of interest (e.g., if your therapist knows another person in your life)',
          ],
        },
      ],
    },
    {
      id: 'understand',
      navLabel: 'Before You Sign',
      heading: 'Before You Sign',
      blocks: [
        {
          type: 'paragraph',
          text: 'By signing this form, you are confirming:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You understand what therapy is and how it works',
            'You understand the potential benefits and risks',
            'You understand that your therapist may need to break confidentiality in certain situations',
            'You have discussed your goals and concerns with your therapist',
            'You have asked questions and received answers',
            'You understand the limits of telehealth (if applicable)',
            "You agree to participate in therapy and to inform your therapist if it's not working",
            'You understand you can stop therapy anytime',
          ],
        },
        {
          type: 'paragraph',
          text: 'Do NOT sign this form unless you understand and agree to these terms.',
        },
      ],
    },
    {
      id: 'sign',
      navLabel: 'Signature',
      heading: 'Your Signature',
      blocks: [
        {
          type: 'field-group',
          fields: [
            {
              type: 'text',
              id: 'printedName',
              label: 'Your Name (print)',
              placeholder: 'Print your name',
              required: true,
              fullWidth: true,
            },
            {
              type: 'select',
              id: 'relationship',
              label: 'Signing as:',
              options: [
                { value: 'self', label: 'Myself (the client)' },
                { value: 'parent', label: 'Parent of client' },
                { value: 'guardian', label: 'Legal Guardian' },
                { value: 'poa', label: 'Power of Attorney' },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'By signing below, I confirm that I have read and understand this Informed Consent for Treatment, and I agree to participate in therapy with {providerName}.',
        },
        { type: 'signature' },
      ],
    },
  ],
  requiredFields: ['firstName', 'lastName', 'dob', 'signature', 'printedName'],
};
